"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import CountrySelector from "@/components/NumberGenerator/CountrySelector";
import RegionSelector from "@/components/NumberGenerator/RegionSelector";
import Image from "next/image";
import ConfirmationModal from "@/components/Modals/ConfirmationModal";
import { createClient } from "@/utils/supabase/client";
import { Hyphenated } from "@/utils/Hyphenated";
import { generateProductSchemas } from "@/lib/esim-product-schema";
import type { EsimPackage } from "@/lib/esim-go";
import { DevicePhoneMobileIcon, BoltIcon, SignalIcon } from "@heroicons/react/24/outline";
import { REGIONS, getRegionById } from "@/data/regions-data";
import { getEsimPackages } from "@/lib/actions/esim.actions";
import ReviewsCarousel from "./ReviewsCarousel";
import { getCountryInfo, type CountryExtendedInfo } from "@/data/country-info";
import CountryInfoCard from "@/components/CountryInfoCard";

type Country = {
  id: number | string;
  country_name: string;
  country_flag?: string;
  country_alpha_code?: string;
};

type Region = {
  id: string;
  name: string;
  emoji: string;
  type: "region";
};

type SelectableItem =
  | (Country & { type?: "country" })
  | (Region & { type: "region" });

export default function EsimRoamingClient({
  countries,
  initialLocation,
}: {
  countries: Country[];
  initialLocation?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [selectionMode, setSelectionMode] = useState<"regions" | "countries">(() => {
    if (initialLocation) {
      if (getRegionById(initialLocation)) return "regions";
      const isCountry = countries.some(
        (c) => Hyphenated(c.country_name) === initialLocation
      );
      if (isCountry) return "countries";
    }
    return "regions";
  });
  const [selectedItem, setSelectedItem] = useState<SelectableItem | null>(() => {
    if (!initialLocation) return null;

    const region = getRegionById(initialLocation);
    if (region) return { ...region, type: "region" };

    const country = countries.find(
      (c) => Hyphenated(c.country_name) === initialLocation
    );
    if (country) return { ...country, type: "country" };

    return null;
  });
  
  const countryInfo = useMemo(() => {
    if (selectedItem?.type === "country" && (selectedItem as Country).country_alpha_code) {
      return getCountryInfo((selectedItem as Country).country_alpha_code!);
    }
    return null;
  }, [selectedItem]);

  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [sortBy, setSortBy] = useState<"none" | "gb" | "duration">("none");

  const [purchaseResult, setPurchaseResult] = useState<any>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [generatingQr, setGeneratingQr] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info" as "info" | "warning" | "danger",
    confirmText: "OK",
    cancelText: "Cancel",
    showCancelButton: true,
    onConfirm: () => {},
  });

  const closeModal = () => {
    setModalOpen(false);
  };

  // Auto-select from URL path or query parameter on mount
  useEffect(() => {
    if (selectedItem || countries.length === 0) return;

    // First, try to get location from URL path (e.g., /global-esim-data-roaming/europe)
    const location = initialLocation || pathname.split('/').pop();

    if (location && location !== 'global-esim-data-roaming') {
      // Check if it's a region
      const region = getRegionById(location);
      if (region) {
        setSelectedItem({ ...region, type: "region" });
        setSelectionMode("regions");
        return;
      }

      // Check if it's a country
      const country = countries.find(
        (c) => Hyphenated(c.country_name) === location
      );
      if (country) {
        setSelectedItem({ ...country, type: "country" });
        setSelectionMode("countries");
        return;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation, pathname, countries]);

  // Update URL when selection changes
  const updateUrl = (item: SelectableItem | null) => {
    if (!item) {
      router.push("/global-esim-data-roaming");
      return;
    }

    const isRegion = item.type === "region";
    const urlSlug = isRegion
      ? (item as Region).id
      : Hyphenated((item as Country).country_name);

    router.push(`/global-esim-data-roaming/${urlSlug}`);
  };

  // Reset selected item when switching modes
  const handleModeChange = (mode: "regions" | "countries") => {
    setSelectionMode(mode);
    setSelectedItem(null);
    setPackages([]);
    setSortBy("none"); // Reset sort when switching modes
    // Update URL without navigation when on base page
    router.push("/global-esim-data-roaming");
  };

  React.useEffect(() => {
    if (!selectedItem) {
      setPackages([]);
      return;
    }

    setLoading(true);
    setPurchaseResult(null);

    // Determine identifier based on selection type
    const isRegion = selectedItem.type === "region";
    const identifier = isRegion
      ? selectedItem.id
      : (selectedItem as Country).country_alpha_code;

    // Use Server Action instead of public API
    getEsimPackages(identifier)
      .then((data) => {
        if (data.packages && Array.isArray(data.packages)) {
          setPackages(data.packages);
        } else {
          setPackages([]);
        }
        if (data.error) {
          console.error("Error fetching packages:", data.error);
        }
      })
      .catch((err) => {
        console.error("Error fetching packages:", err);
        setPackages([]);
      })
      .finally(() => setLoading(false));
  }, [selectedItem]);
  
  // Sort packages based on selected sort option
  const sortedPackages = useMemo(() => {
    if (sortBy === "none" || packages.length === 0) {
      return packages;
    }

    const sorted = [...packages];

    if (sortBy === "gb") {
      sorted.sort((a, b) => {
        // Handle unlimited packages (dataAmount === -1 or isUnlimited flag) - put them at the end
        const aIsUnlimited = a.isUnlimited || a.dataAmount === -1 || a.dataAmount === null || a.dataAmount === undefined;
        const bIsUnlimited = b.isUnlimited || b.dataAmount === -1 || b.dataAmount === null || b.dataAmount === undefined;

        if (aIsUnlimited && bIsUnlimited) return 0;
        if (aIsUnlimited) return 1; // Unlimited goes to end
        if (bIsUnlimited) return -1; // Unlimited goes to end

        // Convert MB to GB for comparison
        // Handle edge cases where dataAmount might be 0 or invalid
        const aDataAmount = a.dataAmount || 0;
        const bDataAmount = b.dataAmount || 0;
        const aGB = aDataAmount / 1024;
        const bGB = bDataAmount / 1024;
        
        return aGB - bGB; // Sort ascending: 1GB, 5GB, 10GB, Unlimited
      });
    } else if (sortBy === "duration") {
      sorted.sort((a, b) => {
        // Handle edge cases where duration might be null/undefined
        const aDuration = a.duration || 0;
        const bDuration = b.duration || 0;
        return aDuration - bDuration; // Sort ascending: 1 day, 7 days, 30 days, etc.
      });
    }

    return sorted;
  }, [packages, sortBy]);

  const handlePurchase = async (pkg: any) => {
    const supabase = createClient();

    // Check if user is logged in
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      setModalConfig({
        title: "Login Required",
        message: "Please login to purchase eSIM packages.",
        type: "warning",
        confirmText: "Go to Login",
        cancelText: "Cancel",
        showCancelButton: true,
        onConfirm: () => {
          window.location.href = "/auth/login";
        },
      });
      setModalOpen(true);
      return;
    }

    // Check user balance
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("balance")
      .eq("id", session.user.id)
      .single();

    if (userError || !userData) {
      setModalConfig({
        title: "Error",
        message: "Failed to fetch your account balance. Please try again.",
        type: "danger",
        confirmText: "Close",
        cancelText: "",
        showCancelButton: false,
        onConfirm: closeModal,
      });
      setModalOpen(true);
      return;
    }

    const currentBalance = userData.balance || 0;
    const price = pkg.price;

    if (currentBalance < price) {
      setModalConfig({
        title: "Insufficient Balance",
        message: `You need $${price.toFixed(
          2
        )} but your current balance is $${currentBalance.toFixed(
          2
        )}. Please top up your account to continue.`,
        type: "warning",
        confirmText: "Top Up",
        cancelText: "Cancel",
        showCancelButton: true,
        onConfirm: () => {
          // Build referral URL with context for eSIM purchase
          const isRegion = selectedItem?.type === "region";
          const locationId = isRegion
            ? selectedItem?.id
            : selectedItem ? Hyphenated((selectedItem as Country).country_name) : '';
          const referralParams = new URLSearchParams({
            ref: 'esim',
            location: locationId || '',
            type: isRegion ? 'region' : 'country',
            package: pkg.id || '',
            price: pkg.price?.toString() || '',
          });
          window.location.href = `/account/add-credit?${referralParams.toString()}`;
        },
      });
      setModalOpen(true);
      return;
    }

    // If all checks pass, show confirmation modal
    setModalConfig({
      title: "Confirm Purchase",
      message: `Are you sure you want to purchase ${pkg.name} for $${pkg.price.toFixed(2)}?`,
      type: "warning",
      confirmText: "Purchase",
      cancelText: "Cancel",
      showCancelButton: true,
      onConfirm: () => executePurchase(pkg),
    });
    setModalOpen(true);
  };

  const executePurchase = async (pkg: any) => {
    setModalOpen(false); // Close confirmation modal
    setPurchasing(true);
    try {
      const res = await fetch("/api/esim/order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package: pkg }),
      });
      const data = await res.json();
      if (res.ok) {
        setPurchaseResult(data.order);
        setQrCodeUrl(null); // Reset QR code for new purchase
        // Success is handled by UI update, no need for alert/modal
      } else {
        setModalConfig({
          title: "Purchase Failed",
          message: `Purchase failed: ${data.error}`,
          type: "danger",
          confirmText: "Close",
          cancelText: "",
          showCancelButton: false,
          onConfirm: closeModal,
        });
        setModalOpen(true);
      }
    } catch (error) {
      console.error("Purchase error:", error);
      setModalConfig({
        title: "Error",
        message: "An error occurred during purchase.",
        type: "danger",
        confirmText: "Close",
        cancelText: "",
        showCancelButton: false,
        onConfirm: closeModal,
      });
      setModalOpen(true);
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-tr to-[#090EDB] from-[#E73DC4] rounded-2xl flex lg:flex-row py-20 flex-col items-start justify-between space-x-1 space-y-4 lg:space-y-0">
        <div className="w-full flex items-center flex-col space-y-5 text-center">
          <h1 className="lg:w-[90%] w-[98%] font-bold text-3xl md:text-5xl text-white">
            {selectedItem
              ? `${
                  selectedItem.type === "region"
                    ? selectedItem.name
                    : (selectedItem as Country).country_name
                } Travel eSIM for Data Roaming`
              : "Global eSIM for Data Roaming in 150+ Countries"}
          </h1>

          <p className="lg:w-[90%] w-[98%] text-gray-300 text-sm md:text-base font-medium">
            Travel the world without worrying about connectivity or shocking
            phone bills. SMSS.biz offers a data roaming eSIM that keeps you
            online around the world at affordable rates.
          </p>

          <div className="w-[95%] md:w-[80%] max-w-md space-y-3">
            {/* Mode Selection Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => handleModeChange("regions")}
                className={`flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  selectionMode === "regions"
                    ? "bg-white text-[#090EDB] shadow-lg scale-105"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                🌍 Regions
              </button>
              <button
                onClick={() => handleModeChange("countries")}
                className={`flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  selectionMode === "countries"
                    ? "bg-white text-[#090EDB] shadow-lg scale-105"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                🗺️ Select Country
              </button>
            </div>

            {/* Selector based on mode */}
            {selectionMode === "regions" ? (
              <RegionSelector
                value={selectedItem?.type === "region" ? selectedItem : null}
                loading={loading}
                handleMenuItemClick={(item) => {
                  setSelectedItem(item);
                  updateUrl(item);
                }}
              />
            ) : (
              <CountrySelector
                countries={countries}
                value={selectedItem?.type === "country" ? selectedItem : null}
                loading={loading}
                handleMenuItemClick={(item) => {
                  setSelectedItem(item);
                  updateUrl(item);
                }}
              />
            )}
          </div>

          <p className="lg:w-[90%] w-[98%] text-gray-300 text-sm md:text-base font-medium">
            💯 Instant activation with QR code delivery
          </p>
        </div>
      </div>

      {/* Country Information Section */}
      {countryInfo && selectedItem?.type === "country" && (
        <div className="pt-8">
          <CountryInfoCard 
            countryInfo={countryInfo} 
            countryName={(selectedItem as Country).country_name} 
          />
        </div>
      )}

      {/* Status/Title Section - Outside gradient background */}
      {purchaseResult ? (
        <div className="pt-14">
          <div className="bg-white p-6 rounded-xl shadow-lg text-left space-y-4 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-green-600">
              ✓ Purchase Successful!
            </h3>
            <p className="text-gray-700">
              <strong>Order Reference:</strong> {purchaseResult.orderReference}
            </p>
            <div className="flex flex-col items-center bg-gray-50 p-4 rounded-lg space-y-4">
              {/* QR Code - Only shown if user generates it */}
              {qrCodeUrl && (
                <>
                  <Image
                    src={qrCodeUrl}
                    alt="eSIM QR Code"
                    width={200}
                    height={200}
                    className="rounded"
                  />
                  <p className="text-sm text-gray-500">
                    Scan this QR code to install your eSIM
                  </p>
                </>
              )}
              
              {/* Installation Links - Default method */}
              <div className="w-full space-y-2">
                <p className="text-sm font-semibold text-gray-700 text-center">
                  Click to install directly:
                </p>
                {purchaseResult.iosInstallUrl && (
                  <a
                    href={purchaseResult.iosInstallUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-black text-white text-center py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors font-semibold"
                  >
                    📱 Install on iOS
                  </a>
                )}
                {purchaseResult.androidInstallUrl && (
                  <a
                    href={purchaseResult.androidInstallUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-green-600 text-white text-center py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    🤖 Install on Android
                  </a>
                )}
              </div>

              {/* Generate QR Code Button */}
              {!qrCodeUrl && (
                <button
                  onClick={async () => {
                    if (!purchaseResult.smdpAddress || !purchaseResult.activationCode) return;
                    
                    setGeneratingQr(true);
                    try {
                      const response = await fetch("/api/esim/qr-code", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          orderReference: purchaseResult.orderReference,
                          smdpAddress: purchaseResult.smdpAddress,
                          activationCode: purchaseResult.activationCode,
                        }),
                      });

                      if (response.ok) {
                        const data = await response.json();
                        setQrCodeUrl(data.qrCodeUrl);
                      } else {
                        console.error("Failed to generate QR code");
                      }
                    } catch (error) {
                      console.error("Error generating QR code:", error);
                    } finally {
                      setGeneratingQr(false);
                    }
                  }}
                  disabled={generatingQr}
                  className="w-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 transition-colors py-2 px-4 rounded-lg text-sm font-semibold text-gray-700"
                >
                  {generatingQr ? "Generating..." : "📷 Generate QR Code"}
                </button>
              )}
            </div>
            <div className="bg-gray-100 p-4 rounded-lg text-sm space-y-2">
              <p className="break-all">
                <strong>SM-DP+ Address:</strong> {purchaseResult.smdpAddress}
              </p>
              <p className="break-all">
                <strong>Activation Code:</strong>{" "}
                {purchaseResult.activationCode}
              </p>
            </div>
            <button
              onClick={() => {
                setPurchaseResult(null);
                setQrCodeUrl(null); // Reset QR code when buying another package
              }}
              className="w-full bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:opacity-90 transition-opacity py-3 text-white font-semibold rounded-lg"
            >
              Buy Another Package
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="pt-20 pb-6 text-gray-600 font-medium text-2xl md:text-3xl">
            {!selectedItem
              ? "Select a country or region to view available packages"
              : loading
              ? ""
              : packages.length > 0
              ? `${packages.length} Data Roaming eSIM packages available for ${
                  selectedItem.type === "region"
                    ? selectedItem.name
                    : (selectedItem as Country).country_name
                }`
              : `Sorry! No Packages Are Available for ${
                  selectedItem.type === "region"
                    ? selectedItem.name
                    : (selectedItem as Country).country_name
                }`}
          </p>

          {packages.length > 0 && !loading && (
            <>
              {/* Sort Dropdown */}
              <div className="pt-6 pb-4 flex justify-end">
                <div className="flex items-center gap-3">
                  <label htmlFor="sort-select" className="text-sm font-medium text-gray-700">
                    Sort by:
                  </label>
                  <select
                    id="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as "none" | "gb" | "duration")}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-[#090EDB] focus:outline-none focus:ring-2 focus:ring-[#090EDB] focus:border-transparent transition-colors cursor-pointer"
                  >
                    <option value="none">Default</option>
                    <option value="gb">Data (GB)</option>
                    <option value="duration">Duration (Days)</option>
                  </select>
                </div>
              </div>

              {/* Google Product Snippets - JSON-LD structured data */}
              {(() => {
                const baseUrl =
                  process.env.NEXT_PUBLIC_BASE_URL ||
                  process.env.NEXT_PUBLIC_DOMAIN_NAME ||
                  "https://smss.biz";
                const productSchemas = generateProductSchemas(
                  sortedPackages as EsimPackage[],
                  baseUrl
                );
                return productSchemas.map((schema, index) => {
                  try {
                    const jsonStr = JSON.stringify(schema).replace(/<\//g, "<\\/");
                    return (
                      <script
                        key={`product-schema-${sortedPackages[index]?.id || index}`}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                          __html: jsonStr,
                        }}
                      />
                    );
                  } catch {
                    return null;
                  }
                });
              })()}
              <div className="gap-4 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-stretch">
                {sortedPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`flex flex-col h-full rounded-xl p-5 shadow-md hover:shadow-lg transition-all border-2 ${
                      pkg.unlimitedType === "ULP"
                        ? "bg-gradient-to-br from-amber-50 to-white border-amber-200 hover:border-amber-400"
                        : "bg-white border-gray-200 hover:border-[#090EDB]"
                    }`}
                  >
                    <div className="flex-1 flex flex-col space-y-3">
                      {/* Title with fixed height */}
                      <h3 className="text-xl font-bold text-gray-900 line-clamp-3 min-h-[5rem]" title={pkg.name}>
                        {pkg.name}
                      </h3>
                      {/* Description with fixed height */}
                      <p className="text-sm text-gray-600 line-clamp-3 min-h-[2.5rem] flex-grow">
                        {pkg.description}
                      </p>
                      {/* Additional Info: Speed, Network Type, Coverage, Compatibility, Activation */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {/* Compatibility badge - always shown */}
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          <DevicePhoneMobileIcon className="w-3.5 h-3.5" />
                          <span>Compatible with Apple iOS and Android</span>
                        </span>
                        {/* Instant activation badge - always shown */}
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <BoltIcon className="w-3.5 h-3.5" />
                          <span>Instant activation</span>
                        </span>
                        {/* Speed badge */}
                        {pkg.unlimitedType === "ULP" ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold shadow-md">
                            <BoltIcon className="w-3.5 h-3.5" />
                            <span>Fastest Speeds</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <SignalIcon className="w-3.5 h-3.5" />
                            <span>{Array.isArray(pkg.speed) ? pkg.speed.join(" / ") : (pkg.speed || "5G / 4G Speed")}</span>
                          </span>
                        )}
                        {pkg.networkType && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <span>📶</span>
                            <span>{pkg.networkType}</span>
                          </span>
                        )}
                        {pkg.coverage && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <span>🌍</span>
                            <span>{pkg.coverage}</span>
                          </span>
                        )}
                      </div>
                      {/* Price section - always at same position */}
                      <div className="flex items-baseline space-x-1 mt-auto">
                        <span className="text-3xl font-bold text-[#090EDB]">
                          ${pkg.price.toFixed(2)}
                        </span>
                        <span className="text-sm text-gray-500">USD</span>
                      </div>
                    </div>
                    {/* Button always at bottom */}
                    <button
                      onClick={() => handlePurchase(pkg)}
                      disabled={purchasing}
                      className="mt-4 w-full bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:opacity-90 transition-opacity px-4 py-3 text-sm font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {purchasing ? "Processing..." : "Buy Now"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <section className="space-y-6 pt-10">
        <h2 className="text-gray-800 font-bold text-3xl md:text-4xl">
          Stay Connected Worldwide with Local Rates
        </h2>
        <p>
          Land in any country and connect to mobile data within minutes, without
          hunting for a local SIM card or paying crazy roaming charges. With an
          SMSS.biz travel eSIM, this is reality. We provide coverage in 150+
          destinations worldwide, so you can be confident you&apos;ll have private,
          secure service virtually anywhere you go. In each country, your eSIM
          connects to local networks at local data rates, not inflated roaming
          prices.
        </p>
      </section>

      <section className="space-y-4 pt-6">
        <h2 className="text-gray-800 font-bold text-2xl md:text-3xl">
          How It Works
        </h2>
        <p>
          One of the best aspects of using an SMSS.biz eSIM is how quick and
          easy it is to set up. You don&apos;t need to be tech-savvy or visit a
          store; if you can scan a QR code or tap a link, you&rsquo;re good to
          go!
        </p>
        <p className="font-semibold">Here&apos;s how to do it:</p>
        <ol className="list-decimal pl-6 space-y-2">
          <li>
            <span className="font-semibold">Check Device Compatibility:</span>{" "}
            First, ensure your smartphone or tablet supports eSIM (most newer
            devices do)
          </li>
          <li>
            <span className="font-semibold">Buy a Data Plan:</span> Purchase an
            eSIM data plan that fits your travel. Select the country or region
            you&apos;re visiting, and choose how much data you want (e.g., 3 GB
            for 7 days, or 5 GB for 30 days). Once you&apos;ve found your
            perfect plan, make sure you have enough credit in your account or
            top-up. There are no contracts or long-term commitments, just a
            one-time prepaid purchase.
          </li>
          <li>
            <span className="font-semibold">Install the eSIM:</span> After
            purchase, you&apos;ll receive simple instructions to install your
            eSIM on your device. We provide a QR code or simple link/file for
            auto setup and step-by-step directions for iOS and Android. It
            usually takes less than 5 minutes to get up and running.
          </li>
        </ol>
        <p>
          If you run low on data, you can easily top up or purchase an add-on
          plan on the fly and it will be delivered instantly to your existing
          eSIM, no new installation needed.
        </p>
      </section>

      <ReviewsCarousel />

      <section className="space-y-4 pt-6">
        <h2 className="text-gray-800 font-bold text-2xl md:text-3xl">
          Flexible Plans & Pricing for Every Traveler
        </h2>
        <p>
          Every traveler&apos;s needs are different. That&apos;s why SMSS.biz offers
          a range of eSIM data plans and transparent pricing to match those
          needs. Pricing depends on the country or region, and the amount of
          data you choose. If you&apos;re visiting one country, you can buy a local
          plan for that country. We have plans for hundreds of individual
          countries. For example, if you&apos;re going to Italy, you might grab an
          Italy eSIM plan with, say, 3 GB for 10 days. Going to Japan for a
          week? Maybe a 5 GB plan for 7 days.
        </p>
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">Ready to Travel Connected?</h3>
          <p>
            With an SMSS.biz eSIM in your phone, you can step off the plane in
            any country and immediately get online. Your maps load, your
            messages sync, your rideshare apps and travel tools all work without
            a hitch. It&apos;s like having a local SIM for every country, without any
            of the effort. It&apos;s definitely the future of travel - one where you
            never have to hunt for connectivity or fear a roaming bill.
          </p>
          <p>
            Whether you&apos;re traveling on business and need reliable email access,
            a travel influencer posting on social media, or a family on vacation
            keeping in touch with loved ones back home, we&apos;ve got you covered.
          </p>
          <p>
            SMSS.biz is your dependable companion for global connectivity. It&apos;s
            fast, affordable, and effortless, exactly what modern travel
            technology should be. So why not give yourself one less thing to
            worry about on your next trip? Join the eSIM revolution and make
            &quot;Where can I get a SIM card?&quot; a question of the past.
          </p>
          <p>
            Get your SMSS.biz eSIM today, and roam the world freely without the
            roaming fees. Wherever you go, your connection travels with you.
            Safe travels and happy surfing!
          </p>
          <p>
            Looking to up your online privacy? Try a{" "}
            <Link href="/free-temporary-numbers" className="underline">
              free temporary number
            </Link>{" "}
            or{" "}
            <Link href="/rent-private-number" className="underline">
              private virtual number rental
            </Link>
            .
          </p>  
        </div>
      </section>

      <section className="space-y-4 pt-6">
        <h2 className="text-gray-800 font-bold text-2xl md:text-3xl">
          eSIM FAQs
        </h2>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">What&apos;s an eSIM?</h3>
          <p>
            Our eSIM is a digital SIM card that you can activate instantly on
            your phone (either iPhone or Android) – no physical SIM swapping
            needed. Stay connected abroad with high-speed data, avoid outrageous
            roaming fees, and enjoy a seamless, travel experience, every time!
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">
            What Is Data Roaming (and Why Avoid It)?
          </h3>
          <p>
            You&apos;ve probably heard horror stories of travelers coming home to
            phone bills in the hundreds or thousands of dollars. The culprit is
            often data roaming. So, what the %$#& is data roaming? In simple
            terms, data roaming is when your phone uses mobile data on a network
            outside your home carrier&apos;s coverage area. In other words, if you
            leave your home country and your phone connects to a foreign carrier
            to use the internet, you are &quot;roaming.&quot; Roaming lets you stay online
            abroad, but it almost always comes with extra charges from your
            carrier. Doomscrolling on TikTok or constant map searches for the
            best sandwich can stack up hefty fees quite quickly.
          </p>
          <p>
            Because of these costs, the general advice is to turn data roaming
            off on your phone whenever you&apos;re overseas, unless you have a
            special plan. If you leave it on, your phone might automatically
            latch onto another network (even near a border at home) and start
            accruing charges without you realizing. Standard roaming rates from
            major carriers can be painfully expensive. For example, many US
            carriers charge around $10–$12 per day for international roaming
            passes, and if you don&apos;t buy a package, pay-as-you-go rates (like
            $0.20 per MB) can lead to a massive bill. In short, international
            roaming is convenient but can burn a hole in your wallet. Travel
            eSIMs are the smart way to avoid these charges entirely.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">
            Why Use an eSIM for International Travel?
          </h3>
          <p>
            Using an eSIM instead of traditional roaming or physical SIM cards
            is a game-changer for travelers. First, let&apos;s clarify what an eSIM
            is: eSIM stands for &quot;embedded SIM,&quot; a tiny chip built into modern
            phones and devices that functions like a SIM card, but without any
            physical card that you have to insert. It allows you to download a
            mobile network profile digitally and get connected anywhere, anytime
            – all with software, no plastic SIM needed. As long as you have an
            eSIM-compatible device, you can use an eSIM to join a new network in
            minutes. Most newer smartphones (iPhone XR/XS and later, Google
            Pixel series, Samsung Galaxy S20 and later, and many others) support
            eSIM. Both iOS and Android have built-in settings to add an eSIM
            plan easily. In short, eSIMs make it possible to activate cellular
            service without visiting a store or swapping tiny cards.
          </p>
          <p>
            For travelers, the benefits of eSIM are huge. Here are a few key
            reasons savvy travelers choose eSIMs over roaming or local SIMs:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <span className="font-semibold">Instant Connectivity:</span> You
              can purchase and activate an eSIM plan before or as soon as you
              land in your destination. No more searching for SIM cards at the
              airport or waiting in line to buy one. With eSIMs, you just scan a
              QR code or click a link, and you&apos;re online in minutes. This means
              you can order a ride or check maps immediately when you arrive.
              It&apos;s a relief to have data working instantly after a long flight.
            </li>
            <li>
              <span className="font-semibold">No Roaming Fees:</span> As
              discussed, eSIMs connect you to local networks. That means you&apos;re
              paying local rates, and you avoid the high roaming fees your home
              carrier would charge. You get all the data you need without fear
              of an annoying financial surprise. It&apos;s a cost-effective solution
              paying a fraction of the cost of equivalent roaming packages. For
              example, instead of paying $10/day for limited roaming, you might
              pay $20 total for 5GB that lasts two weeks on an eSIM. It&apos;s a huge
              saving!
            </li>
            <li>
              <span className="font-semibold">
                Flexible Plans for Your Needs:
              </span>{" "}
              Travel eSIM providers like SMSS.biz offer a wide range of plans to
              fit different trips. You can buy an eSIM for a single country, a
              region (like Europe or Asia), or even a global plan covering many
              countries. Data amounts range from small 1 GB packages up to 10,
              20, or even unlimited data options in some locations. Pricing
              depends on the country and the amount of data you need but you can
              choose exactly what you want so it&apos;s easy to find a plan that&apos;s
              both cheap and sufficient for your travels. This flexibility beats
              a one-size-fits-all roaming plan, hands down.
            </li>
            <li>
              <span className="font-semibold">Dual SIM Convenience:</span> An
              eSIM doesn&apos;t interfere with your regular phone number.
            </li>
          </ul>
          <p>
            You can keep your physical SIM in your phone for calls and texts
            from home, while using the eSIM for data. Your phone simply treats
            the eSIM as a second line dedicated to data. This dual-SIM setup
            means you stay reachable on your primary number (for SMS
            verification texts, bank codes, or emergency calls), but still enjoy
            affordable data through the eSIM. There&apos;s no need to carry a second
            phone or sacrifice your connectivity at home. It&apos;s the best of both
            worlds with one device and two lines. Pro Tip: just make sure to set
            your phone to use the eSIM for mobile data, so you don&apos;t
            accidentally roam on your primary line.
          </p>
          <p>
            In summary, travel eSIMs like SMSS.biz&apos;s give you affordable,
            flexible, and instant mobile data anywhere you go.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">
            Does My Device Work With eSIM?
          </h3>
          <p>
            Most newer devices are eSIM compatible. Apple&apos;s iPhone 12 and later
            all have eSIM functionality, as do Google Pixel models and many
            Samsung, Huawei, and other Android phones. If you have a compatible
            phone, you&apos;re good to go. No need for an unlocked device in most
            cases – eSIM plans are prepaid and separate from your carrier, so
            your phone just needs to allow eSIM.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">
            When Should I Set Up A Travel eSIM?
          </h3>
          <p>
            Before you travel you can set the eSIM as the active data line in
            your phone settings. However, most people activate the eSIM data
            only when they arrive at their destination. With SMSS.biz, you can
            activate the plan at your convenience. As soon as you reach your
            destination, just toggle on the eSIM line and ensure &quot;Cellular Data&quot;
            is set to use it.
          </p>
        </div>
      </section>

      <ConfirmationModal
        isOpen={modalOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        type={modalConfig.type}
        showCancelButton={modalConfig.showCancelButton}
      />
    </>
  );
}
